import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Container,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  addDays,
  differenceInCalendarDays,
  format,
  isValid,
  parseISO,
  startOfDay,
} from 'date-fns';
import api from '../config/api';
import {
  DOCUMENT,
  PAGE_CONTAINER_MAX_WIDTH,
  documentPageContainerSx,
  documentPageTabsSx,
  documentTabIndicatorSx,
  documentOutlineButtonSx,
} from '../constants/documentTheme';
import DocumentPageOverlay, { documentOverlayActionSx } from './DocumentPageOverlay';
import DocumentSectionHead from './DocumentSectionHead';
import ItineraryStop from './ItineraryStop';
import { formatTime24, getDayTimeSpan } from '../utils/itineraryDocumentUtils';

function parseTripDate(value) {
  if (!value) return null;
  try {
    const raw = String(value);
    const iso = raw.includes('T') ? raw : `${raw.slice(0, 10)}T12:00:00`;
    const d = parseISO(iso);
    return isValid(d) ? startOfDay(d) : null;
  } catch {
    return null;
  }
}

function buildItineraryByDays(trip, items) {
  const rows = Array.isArray(items) ? items : [];
  const start = parseTripDate(trip?.start_date);
  const end = parseTripDate(trip?.end_date) || start;
  if (!start) {
    if (!rows.length) return [];
    return [{
      dayNumber: 1,
      date: parseTripDate(rows[0].start_datetime) || new Date(),
      items: rows,
    }];
  }

  const span = Math.max(0, differenceInCalendarDays(end || start, start));
  const days = [];
  for (let i = 0; i <= span && i < 45; i += 1) {
    const date = addDays(start, i);
    const key = format(date, 'yyyy-MM-dd');
    const dayItems = rows.filter((item) => String(item.start_datetime || '').slice(0, 10) === key);
    days.push({ dayNumber: i + 1, date, items: dayItems });
  }

  const datedKeys = new Set(days.map((day) => format(day.date, 'yyyy-MM-dd')));
  const undated = rows.filter((item) => {
    const key = String(item.start_datetime || '').slice(0, 10);
    return !key || !datedKeys.has(key);
  });
  if (undated.length) {
    days[0] = { ...days[0], items: [...days[0].items, ...undated] };
  }
  return days;
}

/**
 * Sandbox-only client trip preview that mirrors the Déjà Vu Nomad trip dashboard look:
 * oxblood hero, document tabs, day-by-day itinerary spine with ItineraryStop.
 */
const SandboxClientTripPreview = ({ open, tripRef, fallbackHref, onClose }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [payload, setPayload] = useState(null);
  const [expandedDays, setExpandedDays] = useState({});
  const [expandedStopIds, setExpandedStopIds] = useState({});

  useEffect(() => {
    if (!open || !tripRef) {
      setPayload(null);
      setError('');
      setExpandedDays({});
      setExpandedStopIds({});
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const res = await api.get(`/developer/sandbox/trips/${encodeURIComponent(tripRef)}`);
        if (!cancelled) {
          setPayload(res.data);
          const trip = res.data?.data?.trip;
          const days = buildItineraryByDays(trip, res.data?.data?.itinerary || []);
          const initial = {};
          days.forEach((day) => {
            initial[day.dayNumber] = true;
          });
          setExpandedDays(initial);
        }
      } catch (err) {
        if (!cancelled) {
          setPayload(null);
          setError(err.response?.data?.error?.message || t('developerPortal.errors.loadFailed'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [open, tripRef, t]);

  const trip = payload?.data?.trip;
  const itinerary = useMemo(
    () => (Array.isArray(payload?.data?.itinerary) ? payload.data.itinerary : []),
    [payload]
  );
  const days = useMemo(() => buildItineraryByDays(trip, itinerary), [trip, itinerary]);
  const plannedStopCount = itinerary.length;

  const dateRangeLabel = useMemo(() => {
    const start = parseTripDate(trip?.start_date);
    const end = parseTripDate(trip?.end_date);
    if (!start) return null;
    if (!end || start.getTime() === end.getTime()) return format(start, 'd MMM yyyy');
    return `${format(start, 'd MMM')} – ${format(end, 'd MMM yyyy')}`;
  }, [trip]);

  const durationDays = useMemo(() => {
    const start = parseTripDate(trip?.start_date);
    const end = parseTripDate(trip?.end_date);
    if (!start || !end) return null;
    return differenceInCalendarDays(end, start) + 1;
  }, [trip]);

  const handleOpenMaps = (item) => {
    const url = item?.google_map_link
      || (item?.location
        ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(item.location)}`
        : null);
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  const toggleDay = (dayNumber) => {
    setExpandedDays((prev) => ({ ...prev, [dayNumber]: prev[dayNumber] === false }));
  };

  const toggleStop = (itemId) => {
    setExpandedStopIds((prev) => ({ ...prev, [itemId]: !prev[itemId] }));
  };

  const actions = (
    <>
      {fallbackHref ? (
        <Button
          href={fallbackHref}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ ...documentOverlayActionSx, ...documentOutlineButtonSx }}
        >
          {t('developerPortal.sandbox.tripPreviewOpenReal')}
        </Button>
      ) : null}
    </>
  );

  return (
    <DocumentPageOverlay
      open={open}
      onClose={onClose}
      title={trip?.title || t('developerPortal.sandbox.tripPreviewTitle')}
      actions={actions}
      testId="sandbox-client-trip-preview"
    >
      <Alert severity="info" sx={{ m: 0, borderRadius: 0 }}>
        {t('developerPortal.sandbox.tripPreviewLoginNote')}
      </Alert>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10, bgcolor: DOCUMENT.canvas }}>
          <CircularProgress sx={{ color: DOCUMENT.oxblood }} />
        </Box>
      ) : null}

      {error ? (
        <Box sx={{ p: 3, bgcolor: DOCUMENT.canvas }}>
          <Alert severity="error" sx={{ borderRadius: 0 }}>{error}</Alert>
        </Box>
      ) : null}

      {!loading && !error && trip ? (
        <Box sx={{ bgcolor: DOCUMENT.canvas, minHeight: '100%' }}>
          {/* Compact oxblood hero — same structure as TripDetails */}
          <Box
            sx={{
              bgcolor: DOCUMENT.oxblood,
              color: DOCUMENT.ivory,
              pt: { xs: 1.5, md: 2 },
              pb: { xs: 2.5, md: 3 },
              px: { xs: 2, md: 0 },
            }}
          >
            <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH} sx={documentPageContainerSx}>
              <Typography
                sx={{
                  fontSize: '0.8rem',
                  color: 'rgba(255,255,255,0.8)',
                  mb: 1.25,
                }}
              >
                {t('developerPortal.sandbox.tripPreviewTitle')}
                {' › '}
                <Box component="span" sx={{ color: DOCUMENT.ivory, fontWeight: 600 }}>
                  {trip.title}
                </Box>
              </Typography>

              <Typography
                sx={{
                  fontFamily: DOCUMENT.display,
                  fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.75rem' },
                  fontWeight: 400,
                  lineHeight: 1.04,
                  color: DOCUMENT.ivory,
                  letterSpacing: '-0.01em',
                }}
              >
                {trip.title}
              </Typography>

              {trip.description ? (
                <Typography
                  sx={{
                    mt: { xs: 0.5, sm: 1 },
                    fontSize: { xs: '0.8125rem', sm: '0.9375rem' },
                    lineHeight: 1.45,
                    color: 'rgba(246,239,228,0.85)',
                    maxWidth: 480,
                  }}
                >
                  {trip.description}
                </Typography>
              ) : null}

              <Box
                component="dl"
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: { xs: '12px 32px', sm: '0 40px' },
                  mt: { xs: 1.5, sm: 2.5 },
                  pt: 2,
                  borderTop: '1px solid rgba(201,169,97,0.28)',
                  mb: 1.5,
                }}
              >
                {dateRangeLabel ? (
                  <Box component="div" sx={{ m: 0 }}>
                    <Typography component="dt" sx={{ fontSize: 10, letterSpacing: '0.19em', textTransform: 'uppercase', color: 'rgba(246,239,228,0.55)', mb: 0.5 }}>
                      {t('trip.dates', { defaultValue: 'Dates' })}
                    </Typography>
                    <Typography component="dd" sx={{ m: 0, fontSize: 15, color: DOCUMENT.ivory, fontVariantNumeric: 'tabular-nums' }}>
                      {dateRangeLabel}
                    </Typography>
                  </Box>
                ) : null}
                {durationDays != null ? (
                  <Box component="div" sx={{ m: 0 }}>
                    <Typography component="dt" sx={{ fontSize: 10, letterSpacing: '0.19em', textTransform: 'uppercase', color: 'rgba(246,239,228,0.55)', mb: 0.5 }}>
                      {t('itinerary.duration')}
                    </Typography>
                    <Typography component="dd" sx={{ m: 0, fontSize: 15, color: DOCUMENT.ivory, fontVariantNumeric: 'tabular-nums' }}>
                      {t('itinerary.day', { count: durationDays })}
                    </Typography>
                  </Box>
                ) : null}
                <Box component="div" sx={{ m: 0 }}>
                  <Typography component="dt" sx={{ fontSize: 10, letterSpacing: '0.19em', textTransform: 'uppercase', color: 'rgba(246,239,228,0.55)', mb: 0.5 }}>
                    {t('trip.statusLabel', { defaultValue: 'Status' })}
                  </Typography>
                  <Typography component="dd" sx={{ m: 0, fontSize: 15, color: DOCUMENT.ivory }}>
                    {t('itinerary.status.upcoming', { defaultValue: 'Upcoming' })}
                  </Typography>
                </Box>
              </Box>

              {(payload?.partner_company_name || trip.partner_company_name || trip.destination) ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mt: 0.5 }}>
                  {trip.destination ? (
                    <Typography sx={{ fontSize: '0.85rem', color: 'rgba(246,239,228,0.75)' }}>
                      {trip.destination}
                    </Typography>
                  ) : null}
                  {(payload?.partner_company_name || trip.partner_company_name) ? (
                    <Chip
                      size="small"
                      label={t('developerPortal.sandbox.tripPreviewManagedBy', {
                        name: payload?.partner_company_name || trip.partner_company_name,
                      })}
                      sx={{
                        borderRadius: 0,
                        bgcolor: 'rgba(255,255,255,0.08)',
                        color: DOCUMENT.ivory,
                        border: '1px solid rgba(201,169,97,0.35)',
                        height: 24,
                      }}
                    />
                  ) : null}
                </Box>
              ) : null}
            </Container>
          </Box>

          <Box sx={{ bgcolor: DOCUMENT.nav, borderBottom: `1px solid ${DOCUMENT.navRule}` }}>
            <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH} sx={{ ...documentPageContainerSx, py: 0 }}>
              <Tabs
                value="itinerary"
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                textColor="inherit"
                TabIndicatorProps={{ sx: documentTabIndicatorSx }}
                sx={documentPageTabsSx}
              >
                <Tab value="itinerary" label={t('tripTabs.itinerary')} />
                <Tab value="memories" label={t('tripTabs.memories')} disabled />
                <Tab value="explore" label={t('tripTabs.explore')} disabled />
                <Tab value="members" label={t('tripTabs.members')} disabled />
                <Tab value="documents" label={t('tripTabs.documents')} disabled />
              </Tabs>
            </Container>
          </Box>

          <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH} sx={{ ...documentPageContainerSx, py: { xs: 3, md: 4 } }}>
            <Box sx={{ px: { xs: 0, md: 1 }, py: { xs: 1, md: 2 } }}>
              <DocumentSectionHead
                sx={{ alignItems: 'center' }}
                title={t('itinerary.dayByDay')}
                meta={t('itinerary.dayByDayMeta', {
                  days: days.length,
                  planned: plannedStopCount,
                })}
              />

              {!days.length ? (
                <Typography sx={{ color: DOCUMENT.ink2, mt: 2 }}>
                  {t('developerPortal.sandbox.tripPreviewEmptyItinerary')}
                </Typography>
              ) : (
                days.map((day) => {
                  const hasItems = day.items.length > 0;
                  const isExpanded = expandedDays[day.dayNumber] !== false;
                  const isOpen = hasItems && isExpanded;
                  const dayLabel = format(day.date, 'EEEE, d MMMM');
                  const timeSpan = getDayTimeSpan(day.items, formatTime24);
                  const dayCardSx = {
                    mt: 1.5,
                    bgcolor: isOpen ? DOCUMENT.surface : DOCUMENT.canvas,
                    border: isOpen
                      ? `1px solid ${DOCUMENT.ruleHard}`
                      : `1px dashed ${DOCUMENT.ruleHard}`,
                  };

                  return (
                    <Box
                      key={day.dayNumber}
                      data-testid={`sandbox-itinerary-day-${day.dayNumber}`}
                      sx={dayCardSx}
                    >
                      <Box
                        onClick={() => hasItems && toggleDay(day.dayNumber)}
                        sx={{
                          display: 'flex',
                          alignItems: 'baseline',
                          gap: 2.25,
                          px: 2.25,
                          pt: hasItems ? 1.75 : undefined,
                          pb: hasItems ? 1.5 : undefined,
                          py: hasItems ? undefined : 1.75,
                          borderBottom: hasItems ? `1px solid ${DOCUMENT.rule}` : 'none',
                          cursor: hasItems ? 'pointer' : 'default',
                          flexWrap: 'wrap',
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 10,
                            letterSpacing: '0.19em',
                            color: hasItems ? DOCUMENT.accent : DOCUMENT.ink3,
                            width: 54,
                            flexShrink: 0,
                          }}
                        >
                          {t('itinerary.dayNumber', {
                            number: String(day.dayNumber).padStart(2, '0'),
                          })}
                        </Typography>
                        <Typography
                          component="h3"
                          sx={{
                            fontFamily: DOCUMENT.display,
                            fontSize: { xs: hasItems ? 18 : 16, sm: hasItems ? 22 : 18 },
                            fontWeight: 400,
                            lineHeight: 1,
                            color: DOCUMENT.ink,
                          }}
                        >
                          {dayLabel}
                        </Typography>
                        <Box
                          sx={{
                            ml: 'auto',
                            display: 'flex',
                            alignItems: 'baseline',
                            gap: 1.5,
                            flexWrap: 'wrap',
                            justifyContent: 'flex-end',
                          }}
                        >
                          {hasItems ? (
                            <Typography sx={{ fontSize: 11.5, color: DOCUMENT.ink3 }}>
                              {t('itinerary.activity', { count: day.items.length })}
                            </Typography>
                          ) : (
                            <Typography sx={{ fontSize: 11.5, color: DOCUMENT.ink3 }}>
                              {t('itinerary.status.openNothingPlanned')}
                            </Typography>
                          )}
                          {timeSpan ? (
                            <Typography sx={{ fontSize: 11.5, color: DOCUMENT.ink3, fontVariantNumeric: 'tabular-nums' }}>
                              {timeSpan}
                            </Typography>
                          ) : null}
                        </Box>
                      </Box>

                      <Collapse in={isOpen} timeout={320}>
                        <Box
                          sx={{
                            px: 2.25,
                            pb: 1.5,
                            '& [data-testid^="itinerary-stop-"]': {
                              borderTop: `1px solid ${DOCUMENT.rule}`,
                            },
                            '& [data-testid^="itinerary-stop-"]:first-of-type': {
                              borderTop: 0,
                            },
                          }}
                        >
                          {day.items.map((item, index) => (
                            <ItineraryStop
                              key={item.id || `${day.dayNumber}-${index}`}
                              item={item}
                              isLast={index === day.items.length - 1}
                              nextItem={day.items[index + 1] || null}
                              currentDayDate={day.date}
                              expanded={Boolean(expandedStopIds[item.id])}
                              onToggle={() => toggleStop(item.id)}
                              onOpenMaps={handleOpenMaps}
                              canEdit={false}
                            />
                          ))}
                        </Box>
                      </Collapse>
                    </Box>
                  );
                })
              )}
            </Box>
          </Container>
        </Box>
      ) : null}
    </DocumentPageOverlay>
  );
};

export default SandboxClientTripPreview;
